import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Collapse, Badge, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane,
  faTimes,
  faChevronDown,
  faChevronUp,
  faPaperclip,
  faMagic,
  faUser,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import toast from 'react-hot-toast';
import axiosInstance from 'helpers/api';
import './InlineEmailComposer.css';

interface EmailRecipient {
  email: string;
  name?: string;
}

interface InlineEmailComposerProps {
  lead: {
    id: string;
    lead_email: string;
    lead_first_name?: string;
    lead_last_name?: string;
  };
  isOpen: boolean;
  onClose: () => void;
  onEmailSent?: () => void;
  defaultSubject?: string;
  defaultMessage?: string;
}

interface RecipientChipProps {
  recipient: EmailRecipient;
  onRemove: () => void;
  variant?: 'primary' | 'secondary' | 'success';
}

const RecipientChip: React.FC<RecipientChipProps> = ({ recipient, onRemove, variant = 'primary' }) => {
  const getInitials = (email: string, name?: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return email.split('@')[0].slice(0, 2).toUpperCase();
  };

  const initials = getInitials(recipient.email, recipient.name);
  const displayName = recipient.name || recipient.email.split('@')[0];

  return (
    <div className={`recipient-chip recipient-chip-${variant}`}>
      <div className="recipient-chip-avatar">
        {initials}
      </div>
      <span className="recipient-chip-name">{displayName}</span>
      <Button
        variant="link"
        size="sm"
        className="recipient-chip-remove"
        onClick={onRemove}
      >
        <FontAwesomeIcon icon={faTimes} />
      </Button>
    </div>
  );
};

const InlineEmailComposer: React.FC<InlineEmailComposerProps> = ({
  lead,
  isOpen,
  onClose,
  onEmailSent,
  defaultSubject = '',
  defaultMessage = ''
}) => {
  const [toRecipients, setToRecipients] = useState<EmailRecipient[]>([]);
  const [ccRecipients, setCcRecipients] = useState<EmailRecipient[]>([]);
  const [bccRecipients, setBccRecipients] = useState<EmailRecipient[]>([]);
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState(defaultMessage);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [ccInputValue, setCcInputValue] = useState('');
  const [bccInputValue, setBccInputValue] = useState('');

  // Initialize with lead email and default values when opening
  useEffect(() => {
    if (isOpen && lead.lead_email) {
      const leadName = [lead.lead_first_name, lead.lead_last_name]
        .filter(Boolean)
        .join(' ');
      setToRecipients([{ 
        email: lead.lead_email, 
        name: leadName || undefined 
      }]);
      
      // Set default values if provided
      if (defaultSubject) {
        setSubject(defaultSubject);
      }
      if (defaultMessage) {
        setMessage(defaultMessage);
      }
    }
  }, [isOpen, lead, defaultSubject, defaultMessage]);

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>, 
    addFn: (email: string) => void,
    clearFn: () => void
  ) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const input = e.currentTarget.value.trim();
      if (input && input.includes('@')) {
        addFn(input);
        clearFn();
      }
    }
  };

  const addRecipient = (type: 'to' | 'cc' | 'bcc', email: string) => {
    const recipient: EmailRecipient = { email };
    switch (type) {
      case 'to':
        setToRecipients([...toRecipients, recipient]);
        break;
      case 'cc':
        setCcRecipients([...ccRecipients, recipient]);
        break;
      case 'bcc':
        setBccRecipients([...bccRecipients, recipient]);
        break;
    }
  };

  const removeRecipient = (type: 'to' | 'cc' | 'bcc', index: number) => {
    switch (type) {
      case 'to':
        setToRecipients(toRecipients.filter((_, i) => i !== index));
        break;
      case 'cc':
        setCcRecipients(ccRecipients.filter((_, i) => i !== index));
        break;
      case 'bcc':
        setBccRecipients(bccRecipients.filter((_, i) => i !== index));
        break;
    }
  };

  const handleFileAttachment = (files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files);
      setAttachments(prev => [...prev, ...fileArray]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleGenerateAI = async () => {
    setIsGeneratingAI(true);
    try {
      // Simulate AI generation - replace with actual AI endpoint
      const leadName = [lead.lead_first_name, lead.lead_last_name]
        .filter(Boolean)
        .join(' ') || 'there';
      
      const aiSubject = `Follow up on ${leadName}'s inquiry`;
      const aiMessage = `<p>Hi ${leadName},</p>
<p>I wanted to follow up on our recent conversation regarding your real estate needs. Based on our discussion, I believe I have some excellent properties that match your criteria.</p>
<p>Would you be available for a brief call this week to discuss these opportunities?</p>
<p>Best regards,<br/>
[Your Name]</p>`;
      
      setSubject(aiSubject);
      setMessage(aiMessage);
      
      toast.success('AI draft generated successfully!');
    } catch (error) {
      toast.error('Failed to generate AI draft');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const handleSend = async () => {
    if (toRecipients.length === 0) {
      toast.error('Please add at least one recipient');
      return;
    }

    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }

    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setIsSending(true);
    try {
      const payload: any = {
        to: toRecipients.map(r => r.email).join(', '),
        subject,
        message,
      };

      if (ccRecipients.length > 0) {
        payload.cc = ccRecipients.map(r => r.email).join(', ');
      }

      if (bccRecipients.length > 0) {
        payload.bcc = bccRecipients.map(r => r.email).join(', ');
      }

      // Handle attachments if any
      if (attachments.length > 0) {
        const formData = new FormData();
        Object.keys(payload).forEach(key => {
          formData.append(key, payload[key]);
        });
        
        attachments.forEach((file) => {
          formData.append('attachments', file);
        });
        
        await axiosInstance.post('emails', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axiosInstance.post('emails', payload);
      }

      toast.success('Email sent successfully!');
      
      // Reset form
      setSubject('');
      setMessage('');
      setAttachments([]);
      setCcRecipients([]);
      setBccRecipients([]);
      setShowCcBcc(false);
      
      // Callback for parent component
      if (onEmailSent) {
        onEmailSent();
      }
      
      // Close composer
      onClose();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to send email';
      toast.error(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Collapse in={isOpen}>
      <div className="inline-email-composer-wrapper">
        <Card className="inline-email-composer">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faEnvelope} className="text-primary" />
              <span className="fw-semibold">Compose Email</span>
            </div>
            <Button
              variant="link"
              size="sm"
              onClick={onClose}
              className="text-muted p-1"
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </Card.Header>
          
          <Card.Body className="p-3">
            {/* Recipients Section */}
            <div className="recipient-section mb-3">
              {/* To Field */}
              <div className="recipient-field mb-2">
                <div className="d-flex align-items-center">
                  <span className="field-label">To:</span>
                  <div className="recipient-input-wrapper flex-grow-1">
                    {toRecipients.map((recipient, index) => (
                      <RecipientChip
                        key={`to-${index}`}
                        recipient={recipient}
                        onRemove={() => removeRecipient('to', index)}
                        variant="primary"
                      />
                    ))}
                    <input
                      type="text"
                      className="recipient-input"
                      placeholder={toRecipients.length === 0 ? "Add recipients" : ""}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, (email) => addRecipient('to', email), () => setInputValue(''))}
                      onBlur={() => {
                        if (inputValue.trim() && inputValue.includes('@')) {
                          addRecipient('to', inputValue.trim());
                          setInputValue('');
                        }
                      }}
                    />
                  </div>
                  {!showCcBcc && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => setShowCcBcc(true)}
                      className="cc-bcc-toggle"
                    >
                      Cc/Bcc
                    </Button>
                  )}
                </div>
              </div>

              {/* CC/BCC Fields */}
              <Collapse in={showCcBcc}>
                <div>
                  {/* CC Field */}
                  <div className="recipient-field mb-2">
                    <div className="d-flex align-items-center">
                      <span className="field-label">Cc:</span>
                      <div className="recipient-input-wrapper flex-grow-1">
                        {ccRecipients.map((recipient, index) => (
                          <RecipientChip
                            key={`cc-${index}`}
                            recipient={recipient}
                            onRemove={() => removeRecipient('cc', index)}
                            variant="secondary"
                          />
                        ))}
                        <input
                          type="text"
                          className="recipient-input"
                          placeholder="Add CC recipients"
                          value={ccInputValue}
                          onChange={(e) => setCcInputValue(e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, (email) => addRecipient('cc', email), () => setCcInputValue(''))}
                          onBlur={() => {
                            if (ccInputValue.trim() && ccInputValue.includes('@')) {
                              addRecipient('cc', ccInputValue.trim());
                              setCcInputValue('');
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* BCC Field */}
                  <div className="recipient-field mb-2">
                    <div className="d-flex align-items-center">
                      <span className="field-label">Bcc:</span>
                      <div className="recipient-input-wrapper flex-grow-1">
                        {bccRecipients.map((recipient, index) => (
                          <RecipientChip
                            key={`bcc-${index}`}
                            recipient={recipient}
                            onRemove={() => removeRecipient('bcc', index)}
                            variant="success"
                          />
                        ))}
                        <input
                          type="text"
                          className="recipient-input"
                          placeholder="Add BCC recipients"
                          value={bccInputValue}
                          onChange={(e) => setBccInputValue(e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, (email) => addRecipient('bcc', email), () => setBccInputValue(''))}
                          onBlur={() => {
                            if (bccInputValue.trim() && bccInputValue.includes('@')) {
                              addRecipient('bcc', bccInputValue.trim());
                              setBccInputValue('');
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Collapse>
            </div>

            {/* Subject Field */}
            <div className="subject-field mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            {/* Message Editor */}
            <div className="message-editor mb-3">
              <ReactQuill
                theme="snow"
                value={message}
                onChange={setMessage}
                placeholder="Write your message here..."
                style={{ height: '200px', marginBottom: '50px' }}
                modules={{
                  toolbar: [
                    ['bold', 'italic', 'underline'],
                    ['link', 'blockquote'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    ['clean']
                  ]
                }}
              />
            </div>

            {/* Attachments */}
            {attachments.length > 0 && (
              <div className="attachments-section mb-3">
                <div className="attachments-header">
                  <span className="text-muted small">Attachments:</span>
                </div>
                <div className="attachments-list">
                  {attachments.map((file, index) => (
                    <div key={index} className="attachment-item">
                      <FontAwesomeIcon icon={faPaperclip} className="text-muted me-2" />
                      <span className="attachment-name">{file.name}</span>
                      <span className="attachment-size">({formatFileSize(file.size)})</span>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => removeAttachment(index)}
                        className="attachment-remove"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="composer-actions d-flex justify-content-between align-items-center">
              <div className="d-flex gap-2">
                <input
                  type="file"
                  multiple
                  onChange={(e) => handleFileAttachment(e.target.files)}
                  style={{ display: 'none' }}
                  id="inline-file-input"
                />
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => document.getElementById('inline-file-input')?.click()}
                  className="d-flex align-items-center gap-1"
                >
                  <FontAwesomeIcon icon={faPaperclip} />
                  Attach
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleGenerateAI}
                  disabled={isGeneratingAI}
                  className="d-flex align-items-center gap-1"
                >
                  <FontAwesomeIcon icon={faMagic} />
                  {isGeneratingAI ? 'Generating...' : 'AI Assist'}
                </Button>
              </div>
              
              <div className="d-flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSend}
                  disabled={isSending || toRecipients.length === 0 || !subject.trim() || !message.trim()}
                  className="d-flex align-items-center gap-1"
                >
                  {isSending ? (
                    <>
                      <Spinner size="sm" animation="border" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPaperPlane} />
                      Send
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    </Collapse>
  );
};

export default InlineEmailComposer;